import axios from 'axios'
import { toast } from 'react-toastify'
import { OAUTH_ENABLED, UNAUTHORIZED } from './Entities/AuthEntities'

const EXCLUDED_AUTH_URLS = [
    '/api/v1/core/Access/access-token',
    '/api/v1/core/Access/refresh-token'
]

let isRefreshing = false
let failedQueue = []

const processQueue = (error, token = null) => {
    failedQueue.forEach(prom => {
        if (error) prom.reject(error)
        else prom.resolve(token)
    })
    failedQueue = []
}

const Axios = (() => {
    const jsonInstance = axios.create({
        headers: {
            'Accept-Language': 'en-US',
            'Content-Type': 'application/json'
        }
    })

    const formDataInstance = axios.create({
        headers: {
            'Accept-Language': 'en-US',
            'Content-Type': 'multipart/form-data'
        }
    })

    const getInstance = (url) => {
        jsonInstance.defaults.baseURL = url
        return jsonInstance
    }

    const getInstanceForFormData = (url) => {
        formDataInstance.defaults.baseURL = url
        return formDataInstance
    }

    const setAccessToken = (token) => {
        if (!OAUTH_ENABLED) return
        if (token) {
            jsonInstance.defaults.headers.common.Authorization = `Bearer ${token}`
            formDataInstance.defaults.headers.common.Authorization = `Bearer ${token}`
        } else {
            delete jsonInstance.defaults.headers.common.Authorization
            delete formDataInstance.defaults.headers.common.Authorization
        }
    }

    const attachInterceptors = (instance, getAccessToken, refreshAccessToken, logout) => {
        instance.interceptors.request.use(
            async (config) => {
                const accessToken = getAccessToken()
                if (OAUTH_ENABLED && !EXCLUDED_AUTH_URLS.some(url => config.url.includes(url))) {
                    if (accessToken) {
                        config.headers.Authorization = `Bearer ${accessToken}`
                    }
                }
                return config
            },
            (error) => Promise.reject(error)
        )

        instance.interceptors.response.use(
            (response) => response,
            async (error) => {
                if (!OAUTH_ENABLED) return Promise.reject(error)

                const originalRequest = error.config
                if (error.response?.status === UNAUTHORIZED && !originalRequest._retry) {
                    if (isRefreshing) {
                        return new Promise((resolve, reject) => {
                            failedQueue.push({ resolve, reject })
                        })
                            .then((token) => {
                                originalRequest.headers.Authorization = `Bearer ${token}`
                                return instance(originalRequest)
                            })
                            .catch((err) => Promise.reject(err))
                    }

                    originalRequest._retry = true
                    isRefreshing = true

                    try {
                        // Always reset to JSON for refresh
                        jsonInstance.defaults.headers['Content-Type'] = 'application/json'
                        const newAccessToken = await refreshAccessToken()

                        if (!newAccessToken) {
                            // No refresh token available - logout
                            processQueue(new Error('No refresh token'), null)
                            logout()
                            return Promise.reject(error)
                        }

                        processQueue(null, newAccessToken)
                        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`
                        return instance(originalRequest)
                    } catch (refreshError) {
                        processQueue(refreshError, null)
                        logout()
                        toast.error('Session expired. Please log in again.')
                        return Promise.reject(refreshError)
                    } finally {
                        isRefreshing = false
                    }
                }
                return Promise.reject(error)
            }
        )
    }

    const setupInterceptors = (getAccessToken, refreshAccessToken, logout) => {
        attachInterceptors(jsonInstance, getAccessToken, refreshAccessToken, logout)
        attachInterceptors(formDataInstance, getAccessToken, refreshAccessToken, logout)
    }

    return {
        getInstance,
        getInstanceForFormData,
        setAccessToken,
        setupInterceptors
    }
})()

export default Axios
